import { useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { estimatePayment, money, vaFundingFeeRate } from "@/lib/format";

const BAH_LOOKUP = "https://www.travel.dod.mil/Allowances/Basic-Allowance-for-Housing/BAH-Rate-Lookup/";
const LOANS = [
  { id: "va", label: "V.A." },
  { id: "conventional", label: "Conventional" },
  { id: "fha", label: "FHA" },
];

const Num = ({ label, value, onChange, step = 1, suffix, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-[13px] font-normal text-neutral-600">{label}</Label>
    <div className="relative">
      <Input type="number" inputMode="decimal" step={step} value={value} onChange={(e) => onChange(Number(e.target.value))}
        className="h-11 bg-white pr-10 text-[15px]" data-testid={testId} />
      {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-neutral-500">{suffix}</span>}
    </div>
  </div>
);

const Row = ({ label, value, pct }) => (
  <div>
    <div className="flex justify-between text-[14px]"><span className="text-neutral-600">{label}</span><span className="font-medium text-ink">{money(value)}</span></div>
    <div className="mt-1.5 h-1 rounded-full bg-neutral-200"><div className="h-1 rounded-full bg-ink transition-[width] duration-500" style={{ width: `${Math.min(100, pct)}%` }} /></div>
  </div>
);

/** Payment estimator with V.A. funding fee and an optional BAH comparison. All figures are estimates. */
export const MortgageCalculator = ({ defaultPrice = 400000, defaultLoan = "va", hoa = 0, showBah = false, testId = "mortgage-calculator" }) => {
  const [price, setPrice] = useState(defaultPrice);
  const [loanType, setLoanType] = useState(defaultLoan);
  const [downPct, setDownPct] = useState(defaultLoan === "va" ? 0 : defaultLoan === "fha" ? 3.5 : 10);
  const [ratePct, setRatePct] = useState(6.5);
  const [years, setYears] = useState(30);
  const [vaUse, setVaUse] = useState("first");
  const [taxPct, setTaxPct] = useState(1.0);
  const [insurance, setInsurance] = useState(125);
  const [bah, setBah] = useState(0);

  const r = useMemo(() => estimatePayment({ price, downPct, ratePct, years, loanType, vaUse, taxPct, insuranceMonthly: insurance, hoaMonthly: hoa }),
    [price, downPct, ratePct, years, loanType, vaUse, taxPct, insurance, hoa]);
  const pick = (id) => {
    setLoanType(id);
    setDownPct(id === "va" ? 0 : id === "fha" ? 3.5 : 10);
  };
  const coverage = bah > 0 ? Math.round((bah / r.total) * 100) : 0;

  return (
    <div className="grid gap-8 rounded-md border border-neutral-200 bg-white p-6 sm:p-8 lg:grid-cols-[1.15fr_1fr]" data-testid={testId}>
      <div className="space-y-6">
        <div className="grid grid-cols-3 rounded-full bg-neutral-100 p-1 text-[14px]">
          {LOANS.map((l) => (
            <button key={l.id} type="button" onClick={() => pick(l.id)} data-testid={`${testId}-loan-${l.id}`}
              className={`rounded-full py-2 transition-colors ${loanType === l.id ? "bg-ink text-white" : "text-neutral-600 hover:text-ink"}`}>
              {l.label}
            </button>
          ))}
        </div>
        <div>
          <div className="flex items-baseline justify-between"><Label className="text-[13px] font-normal text-neutral-600">Home price</Label><span className="text-xl font-medium" data-testid={`${testId}-price`}>{money(price)}</span></div>
          <Slider value={[price]} min={100000} max={1500000} step={5000} onValueChange={([v]) => setPrice(v)} className="mt-3" data-testid={`${testId}-price-slider`} />
        </div>
        <div>
          <div className="flex items-baseline justify-between"><Label className="text-[13px] font-normal text-neutral-600">Down payment</Label><span className="text-[15px] font-medium">{downPct}% · {money(r.down)}</span></div>
          <Slider value={[downPct]} min={0} max={30} step={0.5} onValueChange={([v]) => setDownPct(v)} className="mt-3" data-testid={`${testId}-down-slider`} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Num label="Interest rate (example)" value={ratePct} onChange={setRatePct} step={0.125} suffix="%" testId={`${testId}-rate`} />
          <div className="space-y-1.5">
            <Label className="text-[13px] font-normal text-neutral-600">Loan term</Label>
            <Select value={String(years)} onValueChange={(v) => setYears(Number(v))}>
              <SelectTrigger className="h-11 bg-white text-[15px]" data-testid={`${testId}-term`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="30">30 years</SelectItem><SelectItem value="20">20 years</SelectItem><SelectItem value="15">15 years</SelectItem></SelectContent>
            </Select>
          </div>
          <Num label="Property tax (est.)" value={taxPct} onChange={setTaxPct} step={0.05} suffix="%/yr" testId={`${testId}-tax`} />
          <Num label="Home insurance" value={insurance} onChange={setInsurance} step={5} suffix="$/mo" testId={`${testId}-insurance`} />
        </div>
        {loanType === "va" && (
          <div className="space-y-1.5">
            <Label className="text-[13px] font-normal text-neutral-600">V.A. funding fee</Label>
            <Select value={vaUse} onValueChange={setVaUse}>
              <SelectTrigger className="h-11 bg-white text-[15px]" data-testid={`${testId}-va-use`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first">First use</SelectItem>
                <SelectItem value="subsequent">Subsequent use</SelectItem>
                <SelectItem value="exempt">Exempt (service-connected disability)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="flex flex-col rounded-md bg-mist p-6">
        <p className="text-[13px] uppercase tracking-[0.16em] text-neutral-500">Estimated monthly payment</p>
        <p className="mt-2 font-display text-5xl text-ink" data-testid={`${testId}-total`}>{money(r.total)}</p>
        <div className="mt-6 space-y-4">
          <Row label="Principal & interest" value={r.pi} pct={(r.pi / r.total) * 100} />
          <Row label="Property tax" value={r.tax} pct={(r.tax / r.total) * 100} />
          <Row label="Home insurance" value={r.insurance} pct={(r.insurance / r.total) * 100} />
          {r.mortgageInsurance > 0 && <Row label={loanType === "fha" ? "FHA mortgage insurance" : "PMI (est.)"} value={r.mortgageInsurance} pct={(r.mortgageInsurance / r.total) * 100} />}
          {r.hoa > 0 && <Row label="HOA" value={r.hoa} pct={(r.hoa / r.total) * 100} />}
        </div>
        {loanType === "va" && (
          <p className="mt-5 text-[13px] text-neutral-600">
            V.A. funding fee {vaFundingFeeRate(downPct, vaUse)}% ({money(r.upfrontFee)}) financed into the loan. No PMI on V.A. loans.
          </p>
        )}
        {showBah && (
          <div className="mt-6 border-t border-neutral-200 pt-5">
            <Num label="Your monthly BAH" value={bah || ""} onChange={setBah} step={10} suffix="$/mo" testId={`${testId}-bah`} />
            {bah > 0 && (
              <div className="mt-3" data-testid={`${testId}-bah-result`}>
                <div className="h-2 rounded-full bg-neutral-200"><div className="h-2 rounded-full bg-brass transition-[width] duration-500" style={{ width: `${Math.min(100, coverage)}%` }} /></div>
                <p className="mt-2 text-[14px] text-ink">
                  BAH covers <strong className="font-medium">{coverage}%</strong> of this payment
                  {bah >= r.total ? ` with ${money(bah - r.total)} left over each month.` : ` (${money(r.total - bah)} out of pocket).`}
                </p>
              </div>
            )}
            <a href={BAH_LOOKUP} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-[13px] text-neutral-600 underline underline-offset-4 hover:text-ink" data-testid={`${testId}-bah-link`}>
              Look up your official BAH rate <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
        <p className="mt-auto pt-6 text-[12px] leading-relaxed text-neutral-500">Estimates only, not a loan offer. Ask your lender for today's rates and your exact funding fee.</p>
      </div>
    </div>
  );
};
